import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';

import {
  useCurrentProject,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import JSZip from 'jszip';
import {
  createOrder,
  getCreateOrderPayload,
  getShouldTriggerProjectConfirmationDialog,
  ProductResponse,
} from '../../../../../../../../UILibrary';
import { previewFilesAnalyticsEvents } from '../../../RequestQuote/analytics';
import { ProductDetails } from '../previewFiles.types';
import { CustomFile } from '../previewFiles.types';

type DownloadClickHandlerProps = {
  isSelectingFiles: boolean;
  selectedFiles: CustomFile[];
  selectedFile: CustomFile | null;
  productDetails: ProductDetails | null;
  productSku: string | null;
  product: ProductResponse | null;
  onDownloadComplete?: () => void;
};

export function useDownload() {
  const { isProjectDataComplete } = useValidateProjectData();
  const { currentProject } = useCurrentProject();

  const downloadFile = async (file: CustomFile) => {
    const response = await fetch(file.filename, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${file.filename}`);
    }

    return await response.blob();
  };

  const triggerDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fireAnalyticsAndOrder = (
    project: ProjectModel,
    selectedFiles: CustomFile[],
    selectedFile: CustomFile,
    location: 'digital_sampling_pdp' | 'digital_sampling_download_history',
    productDetails: ProductDetails | null,
    product: ProductResponse | null,
    productSku: string | null,
    taxonomies: string[],
  ) => {
    if (!productDetails?.productSku) {
      return;
    }

    previewFilesAnalyticsEvents.fileDownloaded(
      {
        downloaded_item_count: selectedFiles?.length || 1,
        event_project_name: project?.title,
        event_project_id: project?.externalId?.toString(),
        event_item_id: productSku ?? window.productAttributeJson?.sku?.value,
        event_item_name:
          product?.name ?? window.productAttributeJson?.name?.value,
        event_item_brand:
          product?.custom_attributes?.manufacturer ??
          window.productAttributeJson?.manufacturer?.value,
        event_item_category: taxonomies?.[0],
        event_item_category2: taxonomies?.[0],
        event_item_category3: taxonomies?.[2],
        event_item_variant: window.productAttributeJson?.color?.value,
      },
      location,
    );

    const orderData = getCreateOrderPayload(
      productDetails?.productSku,
      project,
      'Download',
      {
        attachmentPimId: selectedFile?.pim_id ?? selectedFiles[0]?.pim_id,
        attachmentId:
          selectedFile?.attachment_id ?? selectedFiles[0]?.attachment_id,
      },
    );
    createOrder(orderData!);
  };

  const downloadClickHandler = async ({
    isSelectingFiles,
    selectedFiles,
    selectedFile,
    productDetails,
    productSku,
    product,
    onDownloadComplete,
  }: DownloadClickHandlerProps) => {
    const taxonomyValue =
      product?.custom_attributes?.taxonomy_us ??
      window.productAttributeJson?.taxonomy_us?.value;
    const taxonomies = taxonomyValue?.split('.') || [];
    const location = window.location.href?.includes('/digitalcatalogdownloads')
      ? 'digital_sampling_download_history'
      : 'digital_sampling_pdp';

    if (!productDetails?.productSku) {
      return;
    }

    if (!currentProject) {
      window.dispatchEvent(
        new CustomEvent('open-create-project-modal', {
          detail: { eventLocation: 'Digital sampling' },
        }),
      );

      return;
    }

    const shouldTriggerProjectConfirmationDialog =
      getShouldTriggerProjectConfirmationDialog();

    const downloadHandler = async (project: ProjectModel) => {
      if (isSelectingFiles && selectedFiles?.length > 0) {
        // Multi-file download
        const zip = new JSZip();

        for (const file of selectedFiles) {
          if (file?.filename) {
            const blob = await downloadFile(file);
            zip.file(file.name, blob);
          }
        }

        const zipBlob = await zip.generateAsync({ type: 'blob' });

        triggerDownload(zipBlob, 'files.zip');
        onDownloadComplete?.();
      } else if (selectedFile?.filename) {
        // Single file download
        const blob = await downloadFile(selectedFile);

        triggerDownload(blob, selectedFile?.name);
        onDownloadComplete?.();
      }

      const file = selectedFile || selectedFiles[0];
      fireAnalyticsAndOrder(
        project,
        selectedFiles,
        file,
        location,
        productDetails,
        product,
        productSku,
        taxonomies,
      );
    };

    if (shouldTriggerProjectConfirmationDialog) {
      window.dispatchEvent(
        new CustomEvent('open-project-selection-dialog', {
          detail: {
            currentProjectId: currentProject?.externalId?.toString(),
            analyticsEventLocation: 'Digital sampling',
            onCurrentProjectSave: (project: ProjectModel) => {
              // proceed with download
              downloadHandler(project);
            },
          },
        }),
      );

      return;
    }

    // Complete any missing project data
    if (!isProjectDataComplete(currentProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: currentProject?.externalId,
        successCallback: downloadHandler,
      });

      return;
    }

    await downloadHandler(currentProject);
  };

  return { downloadClickHandler };
}
